/**
 * ViewerToolbar Component
 *
 * Toolbar controls for the 3D geometry viewer.
 */

import { useState } from 'react';
import type { ColorScheme, ViewMode, CameraPreset } from '../types';

interface ViewerToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  colorScheme: ColorScheme;
  onColorSchemeChange: (scheme: ColorScheme) => void;
  showNormals: boolean;
  onShowNormalsChange: (show: boolean) => void;
  showGrid: boolean;
  onShowGridChange: (show: boolean) => void;
  showAxes: boolean;
  onShowAxesChange: (show: boolean) => void;
  onCameraPreset: (preset: CameraPreset) => void;
  onFitAll: () => void;
  onZoomToSelection?: (() => void) | undefined;
  selectedCount?: number | undefined;
  onClearSelection?: (() => void) | undefined;
  activeCameraPreset?: CameraPreset | undefined;
}

const viewModeOptions: { value: ViewMode; label: string }[] = [
  { value: 'shaded', label: 'Shaded' },
  { value: 'wireframe', label: 'Wireframe' },
  { value: 'x-ray', label: 'X-Ray' },
];

const colorSchemeOptions: { value: ColorScheme; label: string }[] = [
  { value: 'zone', label: 'By Zone' },
  { value: 'surface-type', label: 'By Surface Type' },
  { value: 'boundary-condition', label: 'By Boundary' },
  { value: 'construction', label: 'By Construction' },
];

/** SVG icons for camera presets */
function IsometricIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}

function TopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="16" rx="1" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
    </svg>
  );
}

function FrontIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="6" width="16" height="12" rx="1" />
      <path d="M4 6l4-3h8l4 3" />
    </svg>
  );
}

function RightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="6" width="12" height="12" rx="1" />
      <path d="M18 6l3 2v8l-3 2" />
    </svg>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="6" width="16" height="12" rx="1" />
      <path d="M4 18l4 3h8l4-3" />
    </svg>
  );
}

function LeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="6" width="12" height="12" rx="1" />
      <path d="M6 6l-3 2v8l3 2" />
    </svg>
  );
}

const cameraPresets: { value: CameraPreset; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'isometric', label: 'Isometric', Icon: IsometricIcon },
  { value: 'top', label: 'Top', Icon: TopIcon },
  { value: 'front', label: 'Front', Icon: FrontIcon },
  { value: 'right', label: 'Right', Icon: RightIcon },
  { value: 'back', label: 'Back', Icon: BackIcon },
  { value: 'left', label: 'Left', Icon: LeftIcon },
];

export function ViewerToolbar({
  viewMode,
  onViewModeChange,
  colorScheme,
  onColorSchemeChange,
  showNormals,
  onShowNormalsChange,
  showGrid,
  onShowGridChange,
  showAxes,
  onShowAxesChange,
  onCameraPreset,
  onFitAll,
  onZoomToSelection,
  selectedCount = 0,
  onClearSelection,
  activeCameraPreset,
}: ViewerToolbarProps) {
  const [currentPreset, setCurrentPreset] = useState<CameraPreset>(activeCameraPreset ?? 'isometric');

  const handleCameraPreset = (preset: CameraPreset) => {
    setCurrentPreset(preset);
    onCameraPreset(preset);
  };

  return (
    <div className="flex flex-wrap items-center gap-4 border-b border-border bg-card px-4 py-2">
      {/* View Mode */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-foreground" id="view-mode-label">View:</label>
        <select
          value={viewMode}
          onChange={(e) => { onViewModeChange(e.target.value as ViewMode); }}
          aria-labelledby="view-mode-label"
          className="rounded border border-border bg-card px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {viewModeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Color Scheme */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-foreground" id="color-scheme-label">Color:</label>
        <select
          value={colorScheme}
          onChange={(e) => { onColorSchemeChange(e.target.value as ColorScheme); }}
          aria-labelledby="color-scheme-label"
          className="rounded border border-border bg-card px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {colorSchemeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Divider */}
      <div className="h-6 w-px bg-border" aria-hidden="true" />

      {/* Toggle options */}
      <div className="flex items-center gap-3" role="group" aria-label="Display options">
        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={showGrid}
            onChange={(e) => { onShowGridChange(e.target.checked); }}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-background"
            aria-label="Show grid"
          />
          <span className="text-sm text-foreground">Grid</span>
        </label>

        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={showAxes}
            onChange={(e) => { onShowAxesChange(e.target.checked); }}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-background"
            aria-label="Show axes"
          />
          <span className="text-sm text-foreground">Axes</span>
        </label>

        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={showNormals}
            onChange={(e) => { onShowNormalsChange(e.target.checked); }}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-background"
            aria-label="Show surface normals"
          />
          <span className="text-sm text-foreground">Normals</span>
        </label>
      </div>

      {/* Divider */}
      <div className="h-6 w-px bg-border" aria-hidden="true" />

      {/* Camera Presets */}
      <div className="flex items-center gap-1" role="group" aria-label="Camera presets">
        <button
          onClick={onFitAll}
          className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
          title="Fit All (Home)"
          aria-label="Fit all geometry in view"
        >
          Fit All
        </button>

        {cameraPresets.map((preset) => (
          <button
            key={preset.value}
            onClick={() => { handleCameraPreset(preset.value); }}
            className={`rounded border p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:ring-offset-background ${
              currentPreset === preset.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
            title={preset.label}
            aria-label={`${preset.label} view`}
            aria-pressed={currentPreset === preset.value}
          >
            <preset.Icon className="h-4 w-4" />
          </button>
        ))}
      </div>

      {/* Selection info */}
      {selectedCount > 0 && (
        <>
          <div className="h-6 w-px bg-border" aria-hidden="true" />
          <div className="flex items-center gap-2" role="status" aria-live="polite">
            <span className="text-sm text-muted-foreground">
              {selectedCount} selected
            </span>
            {onZoomToSelection && (
              <button
                onClick={onZoomToSelection}
                className="rounded border border-border bg-card px-2 py-1 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                title="Zoom to selection (double-click)"
                aria-label="Zoom to selected objects"
              >
                Zoom To
              </button>
            )}
            {onClearSelection && (
              <button
                onClick={onClearSelection}
                className="rounded border border-border bg-card px-2 py-1 text-xs text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                aria-label="Clear selection"
              >
                Clear
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
